import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  HandHeart,
  HeartStraight,
  Users,
  Buildings,
  Gift,
  BookOpen,
  Check,
  Plus,
  Minus,
  ArrowUpRight,
  Info,
} from "@phosphor-icons/react";
import Seo from "../components/Seo";
import MemorialHonorForm from "../components/MemorialHonorForm";

// All donation CTAs currently route to /donate (existing PayPal hosted button).
// We do NOT invent payment URLs. Amount presets, recurring gifts, and
// memorial/honor capture require a separate payment wiring step which the
// user must approve first — see the report below the page component.
const DONATE_ROUTE = "/donate";

const SponsorshipCard = ({
  amount,
  label,
  description,
  testId,
  featured = false,
}) => (
  <div
    data-testid={testId}
    className={`flex flex-col rounded-2xl border p-6 sm:p-7 transition-all duration-300 hover:-translate-y-0.5 ${
      featured
        ? "bg-white border-primary/25 shadow-[0_18px_60px_-24px_rgba(59,110,143,0.35)]"
        : "bg-white border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] hover:shadow-[0_18px_60px_-24px_rgba(15,23,42,0.14)]"
    }`}
  >
    <div className="flex items-baseline gap-2">
      <span className="font-heading text-4xl text-ink-900 font-medium">
        {amount}
      </span>
      {featured && (
        <span className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">
          Most Popular
        </span>
      )}
    </div>
    <p className="mt-1 text-primary font-semibold text-sm uppercase tracking-wide">
      {label}
    </p>
    <p className="mt-3 text-ink-700 text-sm leading-relaxed flex-1">
      {description}
    </p>
    <Link
      to={DONATE_ROUTE}
      data-testid={`${testId}-cta`}
      className="mt-5 inline-flex items-center justify-center gap-2 bg-primary text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-primary-dark hover:shadow-lg transition-all duration-300"
    >
      <HandHeart size={16} weight="fill" />
      Give {amount}
    </Link>
  </div>
);

const PlacementItem = ({ children }) => (
  <li className="flex items-start gap-3 bg-white border border-ink-100 rounded-2xl px-5 py-4 text-ink-800">
    <span
      className="mt-1.5 w-1.5 h-1.5 rounded-full bg-secondary shrink-0"
      aria-hidden="true"
    />
    <span>{children}</span>
  </li>
);

const Step = ({ number, title, body }) => (
  <li className="relative bg-white border border-ink-100 rounded-2xl p-6 sm:p-7">
    <span
      className="absolute -top-3 -left-3 inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-white font-heading text-sm font-semibold shadow-md"
      aria-hidden="true"
    >
      {number}
    </span>
    <h3 className="font-heading text-xl text-ink-900 font-medium leading-snug">
      {title}
    </h3>
    <p className="mt-3 text-ink-700 text-sm leading-relaxed">{body}</p>
  </li>
);

const FaqItem = ({ q, a, testId, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-ink-100 bg-white overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-4 px-5 sm:px-6 py-4 text-left"
      >
        <span className="font-heading text-base sm:text-lg text-ink-900 font-medium leading-snug">
          {q}
        </span>
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-softblue/60 text-primary">
          {open ? (
            <Minus size={14} weight="bold" />
          ) : (
            <Plus size={14} weight="bold" />
          )}
        </span>
      </button>
      {open && (
        <div className="px-5 sm:px-6 pb-5 text-ink-700 text-sm leading-relaxed whitespace-pre-line">
          {a}
        </div>
      )}
    </div>
  );
};

const SponsorABook = () => {
  const [memorialFormOpen, setMemorialFormOpen] = useState(false);
  const navigate = useNavigate();

  // When the memorial form expands, gently scroll it into view.
  React.useEffect(() => {
    if (!memorialFormOpen) return;
    const el = document.getElementById("memorial-honor-form");
    if (el)
      setTimeout(
        () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
        80
      );
  }, [memorialFormOpen]);

  return (
    <div data-testid="page-sponsor-a-book" className="bg-white">
      <Seo
        title="Sponsor a Book · Begin Again Living Ministries"
        description="Help place From Diagnosis to Goodbye into the hands of individuals and families facing serious illness, caregiving, hospice, grief, and loss. A charitable Sponsor a Book program of Begin Again Living Ministries, a 501(c)(3) nonprofit."
      />

      {/* HERO */}
      <section
        className="pt-16 sm:pt-24 pb-16 bg-gradient-to-b from-softblue/40 via-white to-white"
        data-testid="sab-hero"
      >
        <div className="container-x grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Sponsor a Book · Charitable giving program
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              Sponsor a Book. Give a Family Something to Hold Onto.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              Help place <em>From Diagnosis to Goodbye</em> into the hands of
              individuals and families facing serious illness, caregiving,
              hospice, grief, and loss.
            </p>
            <p className="mt-5 text-ink-700 leading-relaxed">
              When a family receives a life-changing diagnosis, there are
              medical appointments, difficult conversations, financial
              pressures, caregiving responsibilities, and questions no one
              feels prepared to answer.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Sometimes what they need is something they can take home, open
              when they&rsquo;re ready, and return to when the next difficult
              question comes.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Through the Sponsor a Book program, your charitable gift to Begin
              Again Living Ministries helps provide copies of{" "}
              <em>From Diagnosis to Goodbye</em> to individuals and families
              who may benefit from them &mdash; at no cost to the recipient.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href="#choose-sponsorship"
                data-testid="sab-hero-cta"
                className="inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                <HandHeart size={18} weight="fill" />
                Sponsor a Book
              </a>
              <Link
                to="/from-diagnosis-to-goodbye"
                className="inline-flex items-center gap-2 border-2 border-primary text-primary rounded-full px-7 py-3.5 font-semibold hover:bg-primary/5 transition-colors"
              >
                <BookOpen size={18} weight="duotone" />
                About the Book
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-ink-100 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.18)]">
              <img
                src="https://images.unsplash.com/photo-1499209974431-9dddcece7f88?auto=format&fit=crop&w=1600&q=80"
                alt="Hands opening a book — a gift being received"
                loading="lazy"
                className="w-full h-[380px] sm:h-[460px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ONE GIFT CAN PUT HOPE INTO SOMEONE'S HANDS */}
      <section className="py-16 sm:py-20 border-t border-ink-100" data-testid="sab-one-gift">
        <div className="container-x max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Why it matters
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
            One gift can put hope into someone&rsquo;s hands.
          </h2>
          <p className="mt-6 text-ink-700 text-lg leading-relaxed">
            Grief doesn&rsquo;t check a family&rsquo;s bank account before it
            arrives.
          </p>
          <p className="mt-4 text-ink-700 leading-relaxed">
            Families facing terminal illness and loss may already be managing
            medical expenses, reduced income, caregiving costs, funeral
            expenses, and countless unexpected needs. Purchasing a grief or
            end-of-life resource may simply not be a priority they can afford.
          </p>
          <p className="mt-4 text-ink-700 leading-relaxed">
            You can help change that. Begin Again Living Ministries uses
            Sponsor a Book contributions to help place{" "}
            <em>From Diagnosis to Goodbye</em> resources with individuals and
            families navigating serious illness, hospice, caregiving, grief,
            and bereavement.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section
        className="py-16 sm:py-20 bg-paper/60 border-y border-ink-100"
        data-testid="sab-how-it-works"
      >
        <div className="container-x">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              How Sponsor a Book Works
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Simple, transparent, and directly for the family.
            </h2>
          </div>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <Step
              number="1"
              title="You Give"
              body="Make a charitable contribution to Begin Again Living Ministries, a 501(c)(3) nonprofit ministry."
            />
            <Step
              number="2"
              title="BALM Places the Books"
              body="Your sponsorship helps Begin Again Living Ministries provide books to people in need through grief-support outreach and participating community organizations such as hospices, chaplains, churches, funeral homes, caregiver programs, and other organizations serving individuals and families."
            />
            <Step
              number="3"
              title="A Family Receives the Resource at No Cost"
              body="The individual or family receiving the sponsored book does not pay for it. The sponsor does not receive books or other goods or services in exchange for the contribution."
            />
          </ol>
        </div>
      </section>

      {/* CHOOSE YOUR SPONSORSHIP */}
      <section
        id="choose-sponsorship"
        className="py-20 sm:py-24 scroll-mt-24"
        data-testid="sab-choose-sponsorship"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Choose Your Sponsorship
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Every level places books directly into hands that need them.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <SponsorshipCard
              amount="$100"
              label="Sponsor 5 Books"
              description="Help place five copies into the hands of individuals or families who need support."
              testId="sab-tier-100"
            />
            <SponsorshipCard
              amount="$500"
              label="Sponsor 25 Books"
              description="Help extend the program to families served through grief-support and community organizations."
              testId="sab-tier-500"
              featured
            />
            <SponsorshipCard
              amount="$1,000"
              label="Sponsor 50 Books"
              description="Make a larger investment in bringing compassionate end-of-life and grief resources into the community."
              testId="sab-tier-1000"
            />
            <SponsorshipCard
              amount="$2,500"
              label="Sponsor 125 Books"
              description="Help provide a substantial supply of resources for community placement and outreach."
              testId="sab-tier-2500"
            />
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 bg-softblue/40 rounded-2xl border border-ink-100 px-6 py-5">
            <div>
              <p className="font-heading text-lg text-ink-900 font-medium">
                Prefer a different amount?
              </p>
              <p className="mt-1 text-ink-600 text-sm">
                You choose the amount on the giving page &mdash; every gift
                helps place more books.
              </p>
            </div>
            <Link
              to={DONATE_ROUTE}
              data-testid="sab-other-amount-cta"
              className="inline-flex items-center gap-2 bg-secondary text-white rounded-full px-6 py-3 font-semibold hover:bg-secondary-dark transition-colors"
            >
              <Gift size={16} weight="fill" />
              Give Another Amount
            </Link>
          </div>
        </div>
      </section>

      {/* MEMORIAL / HONOR */}
      <section className="py-20 sm:py-24" data-testid="sab-memorial-honor">
        <div className="container-x grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="rounded-3xl overflow-hidden border border-ink-100 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.18)]">
              <img
                src="https://images.unsplash.com/photo-1518911710364-17ec553bde5d?auto=format&fit=crop&w=1600&q=80"
                alt="A single candle glowing in warm light"
                loading="lazy"
                className="w-full h-[360px] sm:h-[440px] object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Give in Memory or in Honor
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Let someone&rsquo;s name become part of another family&rsquo;s
              journey.
            </h2>
            <p className="mt-6 text-ink-700 leading-relaxed">
              A Sponsor a Book gift can be made in memory of someone you love
              or in honor of someone whose life has made a difference.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">
              When requested, sponsored copies may include a simple memorial or
              honorary bookplate such as:
            </p>
            <div className="mt-4 grid sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-ink-100 bg-paper/60 px-5 py-4 italic text-ink-800">
                &ldquo;Given in loving memory of ___________&rdquo;
              </div>
              <div className="rounded-2xl border border-ink-100 bg-paper/60 px-5 py-4 italic text-ink-800">
                &ldquo;Given in honor of ___________&rdquo;
              </div>
            </div>
            <p className="mt-5 text-ink-700 leading-relaxed">
              It is a meaningful way to remember one life while helping another
              family through a difficult season.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">
              When appropriate contact information is provided, Begin Again
              Living Ministries may also send an acknowledgment informing the
              person or family being honored that a charitable gift was made.
              The contribution amount will not be disclosed.
            </p>
            {!memorialFormOpen && (
              <button
                type="button"
                onClick={() => setMemorialFormOpen(true)}
                data-testid="sab-memorial-cta"
                className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
              >
                <HeartStraight size={18} weight="fill" />
                Give in Memory or Honor
              </button>
            )}
          </div>
        </div>

        {memorialFormOpen && (
          <div
            id="memorial-honor-form"
            data-testid="sab-memorial-form-panel"
            className="container-x max-w-3xl mt-10 sm:mt-14 scroll-mt-24"
          >
            <MemorialHonorForm onComplete={() => navigate("/donate")} />
          </div>
        )}
      </section>

      {/* WHERE BOOKS MAY GO */}
      <section
        className="py-20 sm:py-24 bg-paper/60 border-y border-ink-100"
        data-testid="sab-placements"
      >
        <div className="container-x">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Where Sponsored Books May Go
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Meet people with compassion wherever they are in the journey.
            </h2>
          </div>
          <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <PlacementItem>Hospice and bereavement programs</PlacementItem>
            <PlacementItem>Hospital and healthcare chaplaincy</PlacementItem>
            <PlacementItem>Churches and faith communities</PlacementItem>
            <PlacementItem>Funeral homes</PlacementItem>
            <PlacementItem>Grief and caregiver support programs</PlacementItem>
            <PlacementItem>Community organizations</PlacementItem>
            <PlacementItem>Chaplains and spiritual-care providers</PlacementItem>
            <PlacementItem>
              Begin Again Living Ministries outreach and support programs
            </PlacementItem>
          </ul>
          <p className="mt-8 max-w-3xl text-ink-700 leading-relaxed">
            Although Begin Again Living Ministries is a faith-based ministry,{" "}
            <em>From Diagnosis to Goodbye</em> is designed to provide
            compassionate, practical support for individuals and families from
            many backgrounds.
          </p>
        </div>
      </section>

      {/* FOR BUSINESSES + ORGANIZATIONS */}
      <section
        className="py-20 sm:py-24"
        data-testid="sab-organizations"
      >
        <div className="container-x max-w-4xl">
          <div className="rounded-3xl border border-ink-100 bg-white p-8 sm:p-12 shadow-[0_18px_60px_-24px_rgba(15,23,42,0.10)]">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <Buildings size={14} weight="duotone" className="text-secondary" />
              For Businesses and Organizations
            </span>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
              Sponsor grief-support resources for the community you serve.
            </h2>
            <p className="mt-6 text-ink-700 leading-relaxed">
              Businesses, hospices, healthcare organizations, funeral homes,
              churches, foundations, and community organizations can support
              the Sponsor a Book program.
            </p>
            <p className="mt-4 text-ink-700 leading-relaxed">
              Organizational sponsorship can help BALM place resources with
              individuals and families who otherwise might not have access to
              them.
            </p>
            <div className="mt-6 rounded-2xl border border-ink-100 bg-softblue/40 px-5 py-4 flex items-start gap-3">
              <Info
                size={18}
                weight="duotone"
                className="text-primary mt-0.5 shrink-0"
              />
              <p className="text-sm text-ink-700 leading-relaxed">
                This is a charitable sponsorship through Begin Again Living
                Ministries. The sponsoring organization does not purchase or
                receive books for its own use through this program.
              </p>
            </div>
            <Link
              to="/contact?topic=general"
              data-testid="sab-organization-cta"
              className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300"
            >
              <Users size={18} weight="duotone" />
              Ask About Organizational Sponsorship
              <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="py-20 sm:py-24 bg-paper/60 border-y border-ink-100"
        data-testid="sab-faq"
      >
        <div className="container-x max-w-3xl">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            Frequently Asked Questions
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-medium text-ink-900 leading-[1.15]">
            Answers before you give.
          </h2>
          <div className="mt-10 space-y-3">
            <FaqItem
              testId="sab-faq-receive"
              defaultOpen
              q="Do I receive books when I sponsor?"
              a={`No. Sponsor a Book is a charitable giving program. Sponsored books are provided to individuals and families through BALM's charitable outreach and participating organizations.

If you or your organization would like to purchase books for your own use, that is a separate book-order transaction rather than a Sponsor a Book contribution.`}
            />
            <FaqItem
              testId="sab-faq-tax"
              q="Is my contribution tax-deductible?"
              a={`Begin Again Living Ministries is a 501(c)(3) nonprofit organization. Contributions are tax-deductible to the extent allowed by law.

BALM will provide appropriate contribution acknowledgment for your records.`}
            />
            <FaqItem
              testId="sab-faq-memory"
              q="Can I sponsor books in someone's memory?"
              a="Yes. You may make your contribution in memory or in honor of someone."
            />
            <FaqItem
              testId="sab-faq-request"
              q="Can I request a particular community or organization?"
              a="You may tell us about a community, organization, or area you would particularly like to support. BALM will consider the request when making placements, subject to program needs, available relationships, and BALM's charitable purposes."
            />
            <FaqItem
              testId="sab-faq-christians"
              q="Are these books only for Christians?"
              a={`No.

Begin Again Living Ministries is a faith-based nonprofit ministry, and faith is an important part of who we are. At the same time, From Diagnosis to Goodbye provides compassionate, practical guidance concerning serious illness, caregiving, hospice, family communication, grief, and loss.

Sponsored resources may serve individuals and families from a variety of backgrounds. We believe compassion should meet people where they are.`}
            />
            <FaqItem
              testId="sab-faq-ownership"
              q="Who owns From Diagnosis to Goodbye?"
              a={`From Diagnosis to Goodbye was created and is owned by Derrick Hall, author and creator of the From Diagnosis to Goodbye educational platform.

Begin Again Living Ministries does not own the intellectual property. BALM makes the books and related resources available as part of its charitable and educational ministry with permission from the author.`}
            />
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="py-24 sm:py-28" data-testid="sab-closing">
        <div className="container-x max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
            <span className="w-8 h-px bg-secondary" />
            <span className="w-8 h-px bg-secondary hidden sm:block" />
          </span>
          <h2 className="mt-5 font-heading text-3xl sm:text-4xl lg:text-5xl font-medium text-ink-900 leading-[1.12]">
            Someone you may never meet could be helped by your gift.
          </h2>
          <div className="mt-8 space-y-3 text-ink-700 leading-relaxed">
            <p>You may never know the family who opens the book.</p>
            <p>You may never sit beside them in the hospital.</p>
            <p>
              You may never hear the difficult conversations happening around
              their kitchen table.
            </p>
            <p className="font-heading text-lg text-ink-900 pt-4">
              But your generosity can still enter that room.
            </p>
            <p>
              Sponsor a book. Help a family feel a little less alone on the
              journey from diagnosis to goodbye.
            </p>
          </div>
          <a
            href="#choose-sponsorship"
            data-testid="sab-closing-cta"
            className="mt-10 inline-flex items-center gap-2 bg-primary text-white rounded-full px-8 py-4 font-semibold text-lg hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300"
          >
            <HandHeart size={20} weight="fill" />
            Sponsor a Book
          </a>
        </div>
      </section>

      {/* FOOTNOTE / DISCLOSURE */}
      <section
        className="pb-16 border-t border-ink-100"
        data-testid="sab-footnote"
      >
        <div className="container-x max-w-3xl pt-8">
          <p className="text-xs text-ink-500 leading-relaxed">
            Sponsor a Book is a charitable program of Begin Again Living
            Ministries. Sponsors do not receive books or other goods or
            services in exchange for their contributions. Sponsored resources
            are distributed through BALM&rsquo;s charitable and educational
            activities. Begin Again Living Ministries is a 501(c)(3) nonprofit
            organization. Contributions are tax-deductible to the extent
            allowed by law.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SponsorABook;
